import React, { useEffect, useMemo, useState } from 'react';
import {
  FormGroup,
  Label,
  InputGroup,
  InputGroupAddon,
  Card,
  CardBody,
} from 'reactstrap';
import { Colxx } from '../common/CustomBootstrap';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';

import Skeleton from 'react-loading-skeleton';
import { ButtonWithLoadingIcon } from '../common/ButtonWithLoadingIcon';
import productAPI from '../../services/API/product';
import { productEditingModes } from '../../constants/productApproval';
import shopAPI from '../../services/API/shop';
import { Status } from '../../constants/status';
import moment from 'moment';
import { useUserContext } from '../../context/UserContext';
import { CustomCardHeader } from '../common/custom-card-header/CustomCardHeader';
import { CustomCheckBox } from '../common/CustomFormComponents';
import '../../container/my-rentals/rentals.scss';
import { statusesData } from '../../container/listing/statusesData';
import { LinkButton } from '../common/LinkButton';

const commonFormValidations = {
  title: Yup.string()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Please enter the product title'),
  price: Yup.number()
    .positive("Please enter valid amount")
    .required('Please enter the product price'),
  // rent_per_week: Yup.number().required(
  //   'Please enter the Rent earning per week'
  // ),
  color: Yup.string().required('Please enter color'),
  size: Yup.string().required('Please set the size'),
  // size_type: Yup.string().required('Please select the size type'),
  // gender: Yup.string()
  //   .oneOf(genderValues, 'Please select a valid gender')
  //   .required('Please select the gender'),
  brand: Yup.string().required('Please enter the brand name'),
  category: Yup.string().required('Please select the category'),
};

const getProductValidationSchema = (editingMode) => {
  let additionalValidations = {};

  return Yup.object().shape({
    ...commonFormValidations,
    ...additionalValidations,
  });
};

// Form related components

const FormRowScaffold = ({
  label,
  id,
  showError,
  error,
  children,
  fieldDescription = '',
  isSM,
  ...other
}) => {
  return (
    <FormGroup row style={{ marginBottom: isSM ? 20 : '1rem' }}>
      <Label
        for={id}
        md={3}
        {...other}
        style={{ fontFamily: 'Brown', paddingBottom: isSM ? 8 : null }}
      >
        {label}
      </Label>
      <Colxx md={9}>
        {children}
        {showError && (
          <div
            className='invalid-feedback d-block'
            style={{ fontFamily: 'Brown' }}
          >
            {error}
          </div>
        )}

        {fieldDescription && (
          <small className='text-muted mb-2 mt-2 d-block'>
            {fieldDescription}
          </small>
        )}
      </Colxx>
    </FormGroup>
  );
};

const SelectFormRow = ({
  label,
  name,
  id,
  options,
  showError,
  error,
  showEmpty,
  isSM,
  selectText,
}) => {
  return (
    <FormRowScaffold
      label={label}
      id={id}
      showError={showError}
      error={error}
      isSM={isSM}
    >
      <Field as='select' name={name} id={id} className='form-control'>
        {showEmpty && <option value=''>{selectText || 'Select'}</option>}
        {options.map((opt, index) => (
          <option value={opt.value} key={`${name}_${index}`}>
            {opt.label}
          </option>
        ))}
      </Field>
    </FormRowScaffold>
  );
};

const CommonInputFormRow = ({
  label,
  name,
  id,
  type,
  showError,
  error,
  isSM,
  ...otherProps
}) => (
  <FormRowScaffold
    label={label}
    id={id}
    showError={showError}
    error={error}
    isSM={isSM}
  >
    <Field
      type={type}
      name={name}
      id={id}
      className='form-control'
      {...otherProps}
    />
  </FormRowScaffold>
);

const InputGroupFormRow = ({
  label,
  showError,
  error,
  prepend,
  append,
  description = '',
  isSM,
  ...other
}) => {
  const [isFocus, setIsFocus] = useState(false);
  return (
    <FormRowScaffold
      label={label}
      id={other.id}
      showError={showError}
      error={error}
      isSM={isSM}
      fieldDescription={other.fieldDescription}
    >
      <InputGroup className='extended-input-group'>
        {prepend && (
          <InputGroupAddon addonType='prepend'>
            <span
              className={
                isFocus ? 'prepand-append-input-focus' : 'prepand-append-input'
              }
            >
              {prepend}
            </span>
          </InputGroupAddon>
        )}
        <Field
          {...other}
          className='extended-input right extended-input left extended-input left form-control'
          onFocus={() => {
            setIsFocus(true);
          }}
          onBlur={() => setIsFocus(false)}
        />

        {append && (
          <InputGroupAddon addonType='append'>
            <span
              className={
                isFocus ? 'prepand-append-input-focus' : 'prepand-append-input'
              }
            >
              {append}
            </span>
          </InputGroupAddon>
        )}
      </InputGroup>
    </FormRowScaffold>
  );
};

// End of form related components

export const ProductDetailsForm = ({
  productData,
  handleSubmit,
  isUpdating,
  submitDisabled,
  formikRef,
  editingMode,
  imageAddError,
  isSM,
  detailPageButtonStates,
  handleFooterButtonClick,
  onCloseModal,
}) => {
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [shopSettings, setshopSettings] = useState({});
  const [priceError, setPriceError] = useState(null);
  // const sizeTypes = [
  //   { label: 'Size', value: 'size' },
  //   { label: 'USSize', value: 'ussize' },
  //   { label: 'Denim', value: 'denim' },
  // ];

  const { user } = useUserContext();

  useEffect(() => {
    let isMounted = true;

    let requests = [
      productAPI.getProductCategories(),
      productAPI.getProductColors(),
      shopAPI.getShop(),
    ];

    // if (editingMode === productEditingModes.ADD_NEW) {
    //   requests.push(shopAPI.getShop());
    // }

    Promise.all(requests)
      .then(([categories, colors, shopSettings]) => {
        if (!isMounted) return;
        setCategories(categories);
        setColors(colors);
        setshopSettings(shopSettings);
      })
      .catch((err) => {
        console.log(err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Formik fields
  const initialValues = {
    title: productData.title || '',
    price: productData.price || '',
    // originalPrice: productData.originalPrice || '',
    rent_per_week: productData.rent_earnings_per_week || '0.00',
    color: productData.color || '',
    size: productData.size || '',
    // size_type: productData.size_type || '',
    gender: productData.gender ? productData.gender.toLowerCase() : '',
    brand: productData.brand || '',
    category: productData.category || '',
    for_sale: productData.for_sale || false,
    return_on_delisting: productData.return_on_delisting || false,
    description:
      productData.description !== 'none' ? productData.description : '',
    commission_percent: shopSettings
      ? shopSettings.consignment_commission_percent
      : '0.00',
    consigned_on: moment().format('yyyy-MM-DD'),
    consigned_by_user_id: user.id,
  };
  const productSchema = useMemo(
    () => getProductValidationSchema(editingMode),
    [editingMode]
  );

  // Component Show/Hide Flaggs
  let canUpdate = categories.length;
  const showProductId = editingMode !== productEditingModes.ADD_NEW;
  const showUpdateButton =
    editingMode !== productEditingModes.ADD_NEW &&
    productData.status === Status.draft_by_lender;

  // handlers
  const onPriceChange = (newVal, setFieldValue) => {
    setFieldValue('price', newVal);
    if (newVal < 0) {
      setPriceError('Please enter valid amount');
    } else {
      if (
        (editingMode === productEditingModes.ADD_NEW ||
          productData.status === Status.draft_by_lender) &&
        shopSettings.rental_cost_per_week_percent
      ) {
        const earningPerWeek =
          (newVal * shopSettings.rental_cost_per_week_percent) / 100;
        setFieldValue('rent_per_week', earningPerWeek);
      }
      setPriceError(null);
    }
  };

  const isFormValid = (errors, touched) => {
    return (
      (errors.category && touched.category) ||
      (errors.title && touched.title) ||
      (errors.brand && touched.brand) ||
      (errors.size && touched.size) ||
      (errors.size_type && touched.size_type) ||
      (errors.color && touched.color) ||
      // (errors.gender && touched.gender) ||
      (errors.price && touched.price) ||
      // (errors.rent_per_week && touched.rent_per_week) ||
      imageAddError
    );
  };

  let statusData = statusesData[productData.status ?? Status.unsaved];
  return (
    <div className='product-approval-form-wrapper'>
      <Formik
        enableReinitialize={true}
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={productSchema}
      >
        {({
          values,
          errors,
          isSubmitting,
          touched,
          setFieldValue,
          setFieldTouched,
        }) => {
          const isError = isFormValid(errors, touched);
          return (
            <Form className='mb-4'>
              <Card
                className='no-shadow wallet-cash-wrapper'
                style={{
                  border: `1px solid #2E2B2B`,
                  backgroundColor: 'transparent',
                  marginBottom: '3rem',
                }}
              >
                <CustomCardHeader title='FILL IN THE DETAILS' />
                <CardBody
                  style={{
                    paddingTop: isSM ? 20 : 32,
                    paddingBottom: isSM ? 20 : 32,
                  }}
                >
                  {showProductId && (
                    <FormRowScaffold label='ID' id='productId' isSM={isSM}>
                      <strong>{productData.id}</strong>
                    </FormRowScaffold>
                  )}
                  <CommonInputFormRow
                    label='Title'
                    type='text'
                    name='title'
                    id='product-title'
                    placeholder={
                      'Something short and sweet works best! Try to include color and style.'
                    }
                    showError={errors.title && touched.title}
                    error={errors.title}
                    isSM={isSM}
                  />
                  <SelectFormRow
                    label='Category'
                    name='category'
                    id='product-category'
                    options={categories.map((cat) => ({
                      label: cat.category,
                      value: cat.category,
                    }))}
                    showError={errors.category && touched.category}
                    error={errors.category}
                    showEmpty
                    isSM={isSM}
                    selectText={'Select category'}
                  />
                  <CommonInputFormRow
                    label='Brand'
                    name='brand'
                    id='product-brand'
                    type='text'
                    showError={errors.brand && touched.brand}
                    error={errors.brand}
                    isSM={isSM}
                  />

                  {/* <SelectFormRow
                    label='Size Type'
                    name='size_type'
                    id='product-size-type'
                    options={sizeTypes}
                    showError={errors?.size_type && touched?.size_type}
                    error={errors?.size_type}
                    showEmpty
                    isSM={isSM}
                    selectText={'Select size type'}
                  /> */}

                  <CommonInputFormRow
                    label='Size'
                    type='text'
                    name='size'
                    id='product-size'
                    showError={errors.size && touched.size}
                    error={errors.size}
                    isSM={isSM}
                  />

                  {/* <CommonInputFormRow
                    label='Color'
                    type='text'
                    name='color'
                    id='product-color'
                    showError={errors.color && touched.color}
                    error={errors.color}
                    isSM={isSM}
                  /> */}

                  <SelectFormRow
                    label='Color'
                    name='color'
                    id='product-color'
                    options={colors.map((color) => ({
                      label: color.color,
                      value: color.color,
                    }))}
                    showError={errors.color && touched.color}
                    error={errors.color}
                    showEmpty
                    isSM={isSM}
                    selectText={'Select color'}
                  />
                  {/* {editingMode !== productEditingModes.ADD_NEW ? (
                  <SelectFormRow
                    label='Gender'
                    name='gender'
                    id='product-gender'
                    options={genders}
                    showError={errors.gender && touched.gender}
                    error={errors.gender}
                    showEmpty
                  />
                ) : null} */}

                  <InputGroupFormRow
                    label='Original retail value'
                    type='number'
                    name='price'
                    id='product-Price'
                    placeholder='0.00'
                    showError={(errors.price && touched.price) || priceError}
                    error={errors.price || priceError}
                    prepend='$'
                    onChange={(e) => {
                      onPriceChange(e.target.value, setFieldValue);
                    }}
                  />

                  <CommonInputFormRow
                    label='Description'
                    type='text'
                    name='description'
                    id='product-description'
                    showError={errors.description && touched.description}
                    error={errors.description}
                    component='textarea'
                    placeholder={`Describe your item! Explain why our members would be interested in renting it, how you've styled it before, or what you love about it.`}
                    rows={5}
                    isSM={isSM}
                  />
                  <FormGroup row>
                    <Label
                      for={'profile-pref-email'}
                      md={3}
                      style={{
                        fontFamily: 'Brown',
                        paddingTop: 0,
                        marginBottom: isSM ? 8 : undefined,
                      }}
                    >
                      {`Available for sale`}
                    </Label>

                    <Colxx md={9}>
                      <span
                        className='d-block mb-2'
                        style={{
                          fontSize: 15,
                          lineHeight: 1.73,
                        }}
                      >
                        Would you like to make your item available for sale? By
                        checking yes, you agree to give up ownership of your
                        item should a member decide to buy it.{' '}
                        <span className='text-muted' style={{ opacity: 0.7 }}>
                          (Optional)
                        </span>
                      </span>
                      <CustomCheckBox
                        value={
                          'Yes, I would like to make my item available for sale.'
                        }
                        name={'profile-pref-email'}
                        title={
                          'Yes, I would like to make my item available for sale.'
                        }
                        id='profile-pref-email'
                        checked={values.for_sale}
                        onCheckChange={(value) => {
                          setFieldValue('for_sale', value);
                        }}
                      />
                    </Colxx>
                  </FormGroup>
                </CardBody>
              </Card>
              {/* <Card
                className='no-shadow wallet-cash-wrapper'
                style={{
                  border: `1px solid #2E2B2B`,
                  backgroundColor: 'transparent',
                  marginBottom: '3.5rem',
                }}
              >
                <CustomCardHeader title='SET YOUR PRICE' />
                <CardBody
                  style={{
                    paddingTop: isSM ? 20 : 32,
                    paddingBottom: isSM ? 20 : 32,
                  }}
                >
                  <span
                    className='d-block'
                    style={{
                      fontSize: 15,
                      lineHeight: 1.73,
                      marginBottom: 32,
                    }}
                  >
                    The selling price is the price a member would pay if they
                    decide to purchase your item and determines your payout when
                    rented or purchased. Please refer to our{' '}
                    <a
                      href='https://commonthreads.com/pages/lending-1'
                      target='_blank'
                      color='link'
                      rel='noreferrer'
                      style={{
                        cursor: 'pointer',
                        color: '#414042',
                        textDecoration: 'underline',
                      }}
                    >
                      <span className='a-link'>pricing table</span>
                    </a>{' '}
                    for help with setting your selling price.
                  </span>
                  <InputGroupFormRow
                    label='Selling Price'
                    type='number'
                    name='price'
                    id='product-Price'
                    placeholder='0.00'
                    showError={errors.price && touched.price}
                    error={errors.price}
                    prepend='$'
                    onChange={(e) => {
                      onPriceChange(e.target.value, setFieldValue);
                    }}
                  />

                  {/* <FormRowScaffold
                    label='For Sale'
                    id='product-for-sale'
                    className='pt-0'
                  >
                    <FormikCustomCheckbox
                      name='for_sale'
                      value={values.for_sale}
                      label='yes'
                      id='product-for-sale'
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                    />
                  </FormRowScaffold> */}
              {/* </CardBody>
              </Card> */}
              {/* <Card
                className='no-shadow wallet-cash-wrapper'
                style={{
                  border: `1px solid #2E2B2B`,
                  backgroundColor: 'transparent',
                  marginBottom: '3.5rem',
                }}
              >
                <CustomCardHeader title='RETURN ASSURANCE' />
                <CardBody
                  style={{
                    paddingTop: isSM ? 20 : 32,
                    paddingBottom: isSM ? 20 : 32,
                  }}
                >
                  <span
                    className='d-block'
                    style={{
                      fontSize: 15,
                      lineHeight: 1.73,
                      marginBottom: 18,
                    }}
                  >
                    You may choose to add Return Assurance if you would like to
                    have your item returned to you should it be rejected,
                    removed, or cancelled. Please note that you will be
                    responsible for any fees that result from shipping your item
                    back to you. If you do not choose to add Return Assurance,
                    your item will be responsibly recycled or donated.
                    (Optional)
                  </span>
                  <CustomCheckBox
                    value={'Add Return Assurance'}
                    name={'return_on_delisting'}
                    title={'Add Return Assurance'}
                    id='add-return-assurance'
                    checked={values.return_on_delisting}
                    onCheckChange={(value) => {
                      setFieldValue('return_on_delisting', value);
                    }}
                    style={{ marginBottom: 15 }}
                  />
                  {/* <CustomCheckBox
                    value={'Responsibly recycle or donate my item'}
                    name={'donate-my-item'}
                    title={'Responsibly recycle or donate my item'}
                    id='donate-my-item'
                    checked={values.for_sale}
                    onCheckChange={(value) => {
                      setFieldValue('for_sale', value);
                    }}
                    style={{ marginBottom: 15 }}
                  /> */}
              {/* </CardBody>
              </Card> */}
              {/* <PDSection title='Details'>
                {showProductId && (
                  <FormRowScaffold label='ID' id='productId'>
                    <strong>{productData.id}</strong>
                  </FormRowScaffold>
                )}

                <CommonInputFormRow
                  label='Title'
                  type='text'
                  name='title'
                  id='product-title'
                  showError={errors.title && touched.title}
                  error={errors.title}
                />

                <SelectFormRow
                  label='Category'
                  name='category'
                  id='product-category'
                  options={categories.map((cat) => ({
                    label: cat.category,
                    value: cat.category,
                  }))}
                  showError={errors.category && touched.category}
                  error={errors.category}
                  showEmpty
                />

                <CommonInputFormRow
                  label='Brand'
                  name='brand'
                  id='product-brand'
                  type='text'
                  showError={errors.brand && touched.brand}
                  error={errors.brand}
                />

                <CommonInputFormRow
                  label='Size'
                  type='text'
                  name='size'
                  id='product-size'
                  showError={errors.size && touched.size}
                  error={errors.size}
                />

                <SelectFormRow
                  label='Color'
                  name='color'
                  id='product-color'
                  options={colors.map((color) => ({
                    label: color.color,
                    value: color.color,
                  }))}
                  showError={errors.color && touched.color}
                  error={errors.color}
                  showEmpty
                />

                {/* {editingMode !== productEditingModes.ADD_NEW ? (
                  <SelectFormRow
                    label='Gender'
                    name='gender'
                    id='product-gender'
                    options={genders}
                    showError={errors.gender && touched.gender}
                    error={errors.gender}
                    showEmpty
                  />
                ) : null} */}

              {/* <FormRowScaffold
                  label='For Sale'
                  id='product-for-sale'
                  className='pt-0'
                >
                  <FormikCustomCheckbox
                    name='for_sale'
                    value={values.for_sale}
                    label='yes'
                    id='product-for-sale'
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                  />
                </FormRowScaffold>
              </PDSection> */}

              {/* <PDSection title='Description'>
                <small className='text-muted mb-2 d-block'>
                  Please add description of your item to help us get better
                  understanding of why our member wants to rent it.
                </small>

                <Field
                  name='description'
                  id='product-description'
                  className='form-control'
                  component='textarea'
                />
              </PDSection> */}

              {/* <PDSection title='Pricing'> */}
              {/* <InputGroupFormRow
                  label='Original Price'
                  type='number'
                  name='originalPrice'
                  id='product-Price'
                  description='fasdf'
                  placeholder='0.00'
                  showError={errors.originalPrice && touched.originalPrice}
                  error={errors.originalPrice}
                  prepend='$'
                /> */}

              {/* <InputGroupFormRow
                  label='Your Price'
                  type='number'
                  name='price'
                  id='product-Price'
                  placeholder='0.00'
                  showError={errors.price && touched.price}
                  error={errors.price}
                  prepend='$'
                  fieldDescription='We recommend setting your price at 10-15% off the original price'
                  onChange={(e) => {
                    onPriceChange(e.target.value, setFieldValue);
                  }}
                /> */}
              {/* {editingMode !== productEditingModes.ADD_NEW ? (
                  <InputGroupFormRow
                    label='Rent earning per week'
                    type='number'
                    name='rent_per_week'
                    placeholder='0.00'
                    id='product-rent-per-week'
                    disabled
                    showError={errors.rent_per_week && touched.rent_per_week}
                    error={errors.rent_per_week}
                    prepend='$'
                  />
                ) : null} */}
              {/* </PDSection> */}

              {isError ? (
                <div style={{ display: 'flex' }}>
                  <span
                    style={{
                      fontSize: '1rem',
                      color: '#F96E4E',
                      fontFamily: 'Brown',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    please fix errors above
                  </span>
                </div>
              ) : null}
              <div
                className='d-flex align-items-center justify-content-center'
                style={{ flexDirection: 'column' }}
              >
                {statusData.buttons.map((statusButton, index) => {
                  const buttonId = statusButton.id;
                  if (!statusButton.isLinkButton) {
                    return (
                      <ButtonWithLoadingIcon
                        key={buttonId}
                        disabled={
                          !isUpdating &&
                          detailPageButtonStates.updatingButton !== ''
                        }
                        loading={
                          detailPageButtonStates.updatingButton === buttonId
                        }
                        color='primary'
                        size='lg'
                        className={'mb-3'}
                        onClick={() => {
                          if (!isUpdating) {
                            handleFooterButtonClick(
                              buttonId,
                              statusButton.target
                            );
                          }
                        }}
                      >
                        {statusButton.name}
                      </ButtonWithLoadingIcon>
                    );
                  } else {
                    return (
                      <div className={'d-flex align-items-center'}>
                        {showUpdateButton ? (
                          canUpdate ? (
                            <LinkButton
                              loading={isUpdating}
                              disabled={submitDisabled}
                              onClick={() => {
                                handleSubmit(values);
                              }}
                              color='primary'
                              size='lg'
                              // className={'mr-3'}
                            >
                              <span className='link-text'>Save as Draft</span>
                            </LinkButton>
                          ) : (
                            <Skeleton width={200} height={50} />
                          )
                        ) : null}
                        {!showUpdateButton ? null : (
                          <span className='mx-2'>or</span>
                        )}
                        {showUpdateButton ? (
                          <LinkButton
                            onClick={() => {
                              handleFooterButtonClick(
                                buttonId,
                                statusButton.target
                              );
                              if (onCloseModal) {
                                onCloseModal();
                              }
                            }}
                            // className={'mr-3'}
                          >
                            <span className='link-text'>
                              {statusButton.name}
                            </span>
                          </LinkButton>
                        ) : null}
                        {showUpdateButton ? null : (
                          <LinkButton
                            onClick={() => {
                              if (onCloseModal) {
                                onCloseModal();
                              }
                            }}
                            className={
                              index === statusData.buttons.length - 1
                                ? ''
                                : 'mr-2'
                            }
                          >
                            <span className='link-text'>Cancel</span>
                          </LinkButton>
                        )}
                        {showUpdateButton ? null : (
                          <span className='mx-2'>or</span>
                        )}
                        {showUpdateButton ? null : (
                          <LinkButton
                            onClick={() => {
                              handleFooterButtonClick(
                                buttonId,
                                statusButton.target
                              );
                            }}
                          >
                            <span className='link-text'>
                              {statusButton.name}
                            </span>
                          </LinkButton>
                        )}
                      </div>
                    );
                  }
                })}
                {/* {!statusData.isNewForm ? (
                  <LinkButton
                    onClick={() => {
                      if (onCloseModal) {
                        onCloseModal();
                      }
                    }}
                  >
                    Cancel
                  </LinkButton>
                ) : null} */}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
